const selectors = {
  storesInput: '[data-store-input]',
  storesList: '[data-store-list]',
  storesMap: '[data-store-map]',
  storeClearButton: '[data-store-clear]',
};

class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.storeList = document.querySelector(selectors.storesList);
    this.storesInput = document.querySelector(selectors.storesInput);
    this.storeClearButton = document.querySelector(selectors.storeClearButton);
    this.storeItemTemplate = this.querySelector('template[name=store-item]').content.firstElementChild;
    this.storeInfowindowTemplate = this.querySelector('template[name=store-infowindow]').content.firstElementChild;
    this.map = null;
    this.markers = [];
    this.latitude = '';
    this.longitude = '';

    this.addGmapScript();

    document.addEventListener('store_item_open', ({ detail }) => {
      if (this.markers[detail.index]) {
        google.maps.event.trigger(this.markers[detail.index], 'click');
      }
    });
    document.addEventListener('store_item_close', () => {
      if (this.infoWindow) {
        this.infoWindow.close();
      }
    });

    this.storeClearButton.addEventListener('click', async () => {
      if (this.storeClearButton.classList.contains('searched')) {
        this.storesInput.value = '';
        this.storeClearButton.classList.remove('searched');
        this.setState('loading');
        await this.geolocateUser();
        this.searchStores();
      }
    });
  }

  geolocateUser() {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.latitude = position.coords.latitude;
          this.longitude = position.coords.longitude;
          resolve();
        },
        () => {
          this.latitude = '';
          this.longitude = '';
          resolve();
        },
        { enableHighAccuracy: false, timeout: 3000 }
      );
    });
  }

  addGmapScript() {
    // Create the script tag, set the appropriate attributes
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${gmapKey}&libraries=places&callback=initMap`;
    script.async = true;

    // Attach your callback function to the `window` object
    window.initMap = async () => {
      this.initMap();
      this.initAutocomplete();

      // Try to geolocate user
      await this.geolocateUser();
      this.searchStores();
    };

    // Append the 'script' element to 'head'
    document.head.appendChild(script);
  }

  initAutocomplete() {
    if (!this.storesInput) { return; }

    // Prevent the form submit when user push the enter key
    this.storesInput.addEventListener('keydown', (event) => {
      if (event.keyCode === 13) {
        event.preventDefault();
      }
    });

    // Create the autocomplete object, restricting the search predictions to
    // geographical location types.
    const autocompleteAddress = new google.maps.places.Autocomplete(this.storesInput, {
      types: ['geocode'],
      fields: ['geometry']
    });

    // When the user selects an address from the drop-down, populate the
    // address fields in the form.
    autocompleteAddress.addListener('place_changed', () => {
      const place = autocompleteAddress.getPlace();
      this.latitude = parseFloat(place.geometry.location.lat());
      this.longitude = parseFloat(place.geometry.location.lng());
      this.storeClearButton.classList.add('searched');
      this.searchStores();
    });

    // Add autocomplete=new-password attribute to remove the autofill on Chrome
    setTimeout(() => {
      this.storesInput.setAttribute('autocomplete', 'new-password');
      if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
        this.storesInput.setAttribute('autocomplete', 'off');
      }
    }, 500);
  }

  initMap() {
    this.map = new google.maps.Map(document.querySelector(selectors.storesMap), {
      center: new google.maps.LatLng(this.latitude, this.longitude),
      zoom: 6,
      mapTypeId: 'roadmap',
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
      },
    });

    // Prepare infowindow var
    this.infoWindow = new google.maps.InfoWindow();
    this.infoWindow.addListener('closeclick', () => {
      document.dispatchEvent(new CustomEvent('store_infowindow_close'));
    });

    const styles = [
      {
        featureType: 'poi',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'road',
        stylers: [{ visibility: 'on' }],
      },
      {
        featureType: 'administrative',
        elementType: 'geometry',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'administrative.country',
        elementType: 'geometry',
        stylers: [{ visibility: 'on' }],
      },
      {
        featureType: 'all',
        stylers: [{ saturation: -100 }],
      },

      {
        featureType: 'water',
        stylers: [{ lightness: 20 }],
      },
      {
        featureType: 'all',
        elementType: 'geometry.stroke',
        stylers: [{ lightness: 30 }],
      },
      {
        featureType: 'administrative.country',
        elementType: 'labels.text',
        stylers: [{ visibility: 'on' }],
      },
      {
        featureType: 'water',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'landscape',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'landscape.natural',
        stylers: [{ visibility: 'on' }],
      },
      {
        featureType: 'landscape.natural.terrain',
        stylers: [{ visibility: 'on' }],
      },
      {
        featureType: 'landscape.natural',
        elementType: 'geometry.fill',
        stylers: [{ color: '#ffffff' }],
      },
      {
        featureType: 'landscape.natural.terrain',
        stylers: [{ lightness: -13 }],
      },
      {
        featureType: 'landscape.natural.terrain',
        stylers: [{ saturation: -70 }],
      },
    ];
    const styledMap = new google.maps.StyledMapType(styles, {
      name: 'Gmap Figaret',
    });
    this.map.mapTypes.set('map_style', styledMap);
    this.map.setMapTypeId('map_style');
  }

  async searchStores() {
    this.setState('loading');
    this.deleteMarkers();
    this.storeList.innerHTML = '';
    const params = {
      latitude: this.latitude,
      longitude: this.longitude,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Europe/Paris'
    };

    try {
      const response = await fetch(`/apps/stores/locations?${new URLSearchParams(params)}`);
      const { data } = await response.json();

      if (data.length) {
        const bounds = new google.maps.LatLngBounds();
        const documentFragment = document.createDocumentFragment();
        data.forEach((store, index) => {
          const latLng = new google.maps.LatLng(
            parseFloat(store.latitude),
            parseFloat(store.longitude),
          );
          bounds.extend(latLng);
          this.createMarker(latLng, store, index);
          documentFragment.appendChild(this.createStoreItem(store));
        });
        this.storeList.appendChild(documentFragment);

        // Create custom marker clusterer renderer
        const renderer = {
          render: ({ count, position }) => {
            const svg = window.btoa(`
              <svg fill="#000" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                <circle cx="120" cy="120" r="70" />
              </svg>`);

            return new google.maps.Marker({
              position,
              icon: {
                url: `data:image/svg+xml;base64,${svg}`,
                scaledSize: new google.maps.Size(68, 68),
              },
              label: {
                text: String(count),
                color: "#FFF",
                fontSize: "15px",
              },
              // adjust zIndex to be above other markers
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            });
          }
        };

        // Add a marker clusterer to manage the markers.
        new markerClusterer.MarkerClusterer({
          markers: this.markers,
          map: this.map,
          renderer,
        });

        this.map.fitBounds(bounds);
        this.setState('success');
      } else {
        this.setState('empty');
      }
    } catch (error) {
      console.error(error);
      this.setState('error');
    }
  }

  createMarker(latLng, store, index) {
    let storeInfowindow = this.storeInfowindowTemplate.cloneNode(true);
    const marker = new google.maps.Marker({
      map: this.map,
      icon: {
        url: markerUrl,
        size: new google.maps.Size(34, 34),
        scaledSize: new google.maps.Size(34, 34),
      },
      position: latLng,
    });

    // on Marker click
    storeInfowindow = this.replaceVars(storeInfowindow, store);
    google.maps.event.addListener(marker, 'click', () => {
      // Open infoWindow
      this.infoWindow.setContent(storeInfowindow);
      this.infoWindow.open(this.map, marker);

      document.dispatchEvent(new CustomEvent('store_infowindow_open', { detail: { index } }));

      // Scroll to the selected store item
      const selectedStoreItem = document.querySelector(`[data-marker='${index}']`);
      if (selectedStoreItem) {
        selectedStoreItem.classList.add('active');

        this.storeList.scrollTo(selectedStoreItem.offsetTop);
      }
    });

    this.markers.push(marker);

    return this.markers.length - 1;
  }

  createStoreItem(store) {
    let storeItem = this.storeItemTemplate.cloneNode(true);
    storeItem = this.replaceVars(storeItem, store);

    return storeItem;
  }

  deleteMarkers() {
    if (this.markers.length) {
      this.markers.forEach((marker) => {
        marker.setMap(null);
      });
      this.markers = [];
    }
  }

  replaceVars(node, store) {
    const vars = {
      name: store.name,
      address: store.address,
      zipcode: store.zip_code,
      city: store.city,
      country: store.country,
      latitude: store.latitude,
      longitude: store.longitude,
      phone: store.phone_number,
      phone_formatted: store.phone_number.replace(/\s/g, ''),
      handle: store.handle,
      status: store.is_open ? translations.open : (store.closed ? translations.temporarily_closed : ''),
      statusClass: store.is_open ? 'open' : 'closed',
      image: store.image ? `https://figaret-stores.app-sym.com/storage/${store.image}` : '',
    };

    // Services
    vars.services = store.services ? `<li>${store.services.map(service => (translations.services[service.id]) ? translations.services[service.id] : '').join('</li><li>')}</li>` : '';
    if (store.enable_cc) {
      vars.services += `<li>${translations.services.cc}</li>`;
    }

    const elementsToRemoveIfEmpty = ['image', 'phone_number', 'open_hours', 'services'];
    elementsToRemoveIfEmpty.forEach(elementClass => {
      if (!store[elementClass]) {
        Array.from(node.querySelectorAll(`.${elementClass}`)).forEach(element => {
          element.remove();
        });
      }
    });

    if (store.open_hours) {
      let hours = '';
      const currentDay = (new Date()).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      Object.keys(translations.days).forEach(day => {
        if (store.open_hours[day]) {
          const tag = currentDay == day ? 'strong' : 'span';
          const row = document.createElement('li');
          const dayTag = document.createElement(tag);
          const hourTag = document.createElement(tag);
          dayTag.innerHTML = translations.days[day];
          hourTag.innerHTML = store.open_hours[day].length ? store.open_hours[day] : translations.closed;
          row.append(dayTag, hourTag);
          hours += row.outerHTML;
        }
      });
      vars.hours = hours;
    }

    node.innerHTML = node.innerHTML.replace(
      /{{\s?(\w*)\s?}}/g,
      function (m, key) {
        return vars.hasOwnProperty(key) ? vars[key] : "";
      }
    );

    return node;
  }

  setState(state) {
    this.dataset.state = state;
  }
}
customElements.define('store-locator', StoreLocator);

class StoreItem extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.index = [].slice.call(this.parentNode.parentNode.children).indexOf(this.parentNode);

    this.initListeners();
  }

  initListeners() {
    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.isOpen()) {
        this.close();
        document.dispatchEvent(new CustomEvent('store_item_close'));
      } else {
        this.open();
        document.dispatchEvent(new CustomEvent('store_item_open', { detail: { index: this.index } }));
      }
    });

    document.addEventListener('store_infowindow_open', ({ detail }) => {
      if (this.index === detail.index) {
        this.open();
      } else {
        this.close();
      }
    });

    document.addEventListener('store_infowindow_close', () => {
      if (this.isOpen()) {
        this.close();
      }
    });
  }

  isOpen() {
    return this.details.hasAttribute('open');
  }

  open() {
    this.details.setAttribute('open', '');
  }

  close() {
    this.details.removeAttribute('open');
  }
}
customElements.define('store-item', StoreItem);